import React, { useState, useEffect } from 'react';
import { ExternalLink, Youtube, Loader2, Eye, Clock } from 'lucide-react';
import { Button } from './ui/button';

const VideoGallery = () => {
  const [topVideos, setTopVideos] = useState([]);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    cargarTopVideos();
  }, []);

  // Auto-rotación cada 5 segundos (ralentizado)
  useEffect(() => {
    if (topVideos.length > 0 && !error) {
      const interval = setInterval(() => {
        setCurrentVideoIndex((prevIndex) => (prevIndex + 1) % topVideos.length);
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [topVideos, error]);

  const cargarTopVideos = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/videos/top`);
      const data = await response.json();

      if (data.success && data.videos.length > 0) {
        setTopVideos(data.videos);
        setError(false);
      } else {
        setError(true);
      }
    } catch (err) {
      console.error('Error cargando videos top:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const openYoutubeChannel = () => {
    window.open('https://www.youtube.com/@reparatucuerpo', '_blank');
  };

  const currentVideo = topVideos[currentVideoIndex];

  return (
    <section id="videos" className="py-16 md:py-24 bg-[var(--bg-subtle)]">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Youtube size={40} className="text-[var(--brand-dark)]" />
            <h2 className="heading-1">Videos más populares</h2>
          </div>
          <p className="body-large max-w-2xl mx-auto mb-6">
            Aprende ejercicios y automasajes para aliviar tu dolor. Los videos más vistos de mi canal.
          </p>
          <Button className="btn-secondary" onClick={openYoutubeChannel}>
            <ExternalLink className="mr-2 h-4 w-4" />
            Ver todos los videos
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="animate-spin text-[var(--brand-dark)]" size={48} />
            <span className="ml-4 body-large">Cargando videos...</span>
          </div>
        ) : currentVideo ? (
          <div className="max-w-4xl mx-auto">
            {/* Reproductor de video embebido */}
            <div className="relative aspect-video rounded-3xl overflow-hidden shadow-2xl mb-6 network-card">
              <iframe
                src={`https://www.youtube.com/embed/${currentVideo.videoId}?autoplay=0&rel=0`}
                title={currentVideo.titulo}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              ></iframe>
            </div>

            {/* Información del video */}
            <div className="text-center">
              <h3 className="heading-2 mb-3">{currentVideo.titulo}</h3>
              <div className="flex items-center justify-center gap-6 body-medium text-[var(--text-light)]">
                <span className="inline-flex items-center gap-2">
                  <Eye size={18} />
                  {currentVideo.vistas?.toLocaleString('es-ES')} vistas
                </span>
                <span className="inline-flex items-center gap-2">
                  <Clock size={18} />
                  {currentVideo.duracion}
                </span>
              </div>

              {/* Indicadores de video (1, 2, 3) */}
              <div className="flex items-center justify-center gap-3 mt-6">
                {topVideos.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentVideoIndex(index)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      index === currentVideoIndex 
                        ? 'bg-[var(--brand-dark)] scale-125' 
                        : 'bg-gray-400 hover:bg-gray-600'
                    }`}
                    aria-label={`Ver video ${index + 1}`}
                  />
                ))}
              </div>
              <p className="body-small text-[var(--text-light)] mt-2">
                Cambia automáticamente cada 5 segundos
              </p>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="body-large text-[var(--text-light)]">
              No se pudieron cargar los videos en este momento. Por favor, visita el canal directamente.
            </p>
            <Button className="btn-primary mt-4" onClick={openYoutubeChannel}>
              <Youtube className="mr-2 h-5 w-5" />
              Ir al canal de YouTube
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default VideoGallery;
